/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HmfActionTextComponent, HmfSelectableComponent } from '@dsg-tech/homefield-angular';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconFulfillmentInStoreShoppingFill,
  homefieldIconFulfillmentPickupFill,
  homefieldIconFulfillmentPickupOutline,
  homefieldIconFulfillmentSamedayFill,
  homefieldIconFulfillmentSamedayOutline,
  homefieldIconFulfillmentShippingFill,
  homefieldIconFulfillmentShippingOutline,
} from '@dsg-tech/homefield-angular-icons';

import { FulfillmentSectionHeaders, SelectableOption } from '@plp/data-access/interfaces';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { injectFocusRestore } from '@plp/utils';
import { selectableShippingOptions } from '@plp/data-access/constants';

@Component({
  selector: 'plp-components-fulfillment-filter-selectable',
  imports: [
    HmfSelectableComponent,
    HomefieldIconLibraryModule,
    NgTemplateOutlet,
    TitleCasePipe,
    HmfActionTextComponent,
  ],
  templateUrl: './fulfillment-filter-selectable.component.html',
  styleUrl: './fulfillment-filter-selectable.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FulfillmentFilterSelectableComponent {
  private readonly homefieldIconService = inject(HomefieldIconsLibraryService);
  private readonly focusRestore = injectFocusRestore(computed(() => !this.disabled()));

  readonly sectionHeaders = input<FulfillmentSectionHeaders>({
    pickup: { label: '', value: '' },
    ship: { label: '', value: '' },
  });

  readonly currentSelectedOptions = input<string[]>([]);
  readonly fulfillmentFacets = input<Record<string, number>>({});
  readonly fulfillmentFacetsEnabled = input(false);
  readonly sameDayDeliveryEnabled = input(false);
  readonly disabled = input(false);

  readonly selectedOption = output<string>();
  readonly changeLocationAndStoreName = output<'pickup' | 'ship'>();

  readonly hasFulfillmentData = computed(() => Object.keys(this.fulfillmentFacets()).length > 0);

  readonly ALL_SELECTABLE_OPTIONS: SelectableOption[] = Object.entries(
    selectableShippingOptions,
  ).map(([key, value]) => ({ key, ...value }));

  private readonly PICKUP_KEYS = ['BOPIS', 'ISA'];
  private readonly SHIP_KEYS = ['SDD', 'SHIP'];

  readonly pickupOptions = computed(() => {
    const isSDDEnabled = this.sameDayDeliveryEnabled() ?? false;
    return this.ALL_SELECTABLE_OPTIONS.filter((opt) =>
      isSDDEnabled ? opt.key === 'BOPIS' : this.PICKUP_KEYS.includes(opt.key),
    );
  });

  readonly shipOptions = computed(() => {
    const isSDDEnabled = this.sameDayDeliveryEnabled() ?? false;
    return this.ALL_SELECTABLE_OPTIONS.filter((opt) =>
      isSDDEnabled ? this.SHIP_KEYS.includes(opt.key) : opt.key === 'SHIP',
    );
  });

  readonly isOptionDisabled = computed(() => {
    const facetsEnabled = this.fulfillmentFacetsEnabled();
    const hasData = this.hasFulfillmentData();
    const facets = this.fulfillmentFacets();
    const globalDisabled = this.disabled();

    return (key: string) =>
      globalDisabled || (facetsEnabled && hasData && (facets[key] ?? 0) === 0);
  });

  constructor() {
    this.homefieldIconService.registerIcons([
      homefieldIconFulfillmentPickupFill,
      homefieldIconFulfillmentShippingFill,
      homefieldIconFulfillmentSamedayFill,
      homefieldIconFulfillmentShippingOutline,
      homefieldIconFulfillmentPickupOutline,
      homefieldIconFulfillmentSamedayOutline,
      homefieldIconFulfillmentInStoreShoppingFill,
    ]);
  }

  handleOptionSelection(optionKey: string): void {
    // Capture focus now so it can be restored once the option re-enables.
    this.focusRestore.capture();
    this.selectedOption.emit(optionKey);
  }
}
